/**
 * Feature flags
 *
 * Simple compile-time / module-level feature flag constants. Used to gate
 * subsystems that we want to be able to disable without removing the code.
 */

export const featureFlags = {
  /**
   * Kill switch for the decryption retry **scheduling** layer:
   *   - the retry orchestrator (`runDecryptionRetryPass` and its triggers),
   *   - the one-time legacy-row repair startup task.
   *
   * When `false`, neither runs — already-failed rows stay in `failed` state
   * forever and the legacy "Could not decrypt message" sentinel rows are
   * not migrated.
   *
   * Intentionally NOT gated by this flag (these are unconditional behavior
   * changes from this PR, not opt-in):
   *   - typed `DecryptionError` thrown by the crypto module,
   *   - `decryptionStatus` / `decryptionAttempts` persisted on
   *     `MessageEntity` and `ChatEntity.lastMessage`,
   *   - the parser rendering "Decrypting…" / "Unable to decrypt — tap to
   *     report" based on those fields.
   */
  decryptionRetry: true,

  /**
   * Chunked upload and SW-streamed playback for large videos.
   *
   * When `false` (default), all uploads use the single-blob path regardless
   * of file size, and the attachment viewer uses the existing media path for
   * all videos. Flip to `true` once CHUNKED_UPLOAD_ENABLED=true is set on
   * the backend.
   */
  chunkedUpload: import.meta.env.VITE_CHUNKED_UPLOAD_ENABLED === "true",

  /**
   * Phase 4 sync/reconciliation engine.
   *
   * When `true`, the new sync engine (syncEngine + syncLock + useSync) is
   * active — delta sync via Umbra V2 endpoints replaces the legacy forced
   * reconciliation hook. Defaults to `false`; enable by setting
   * `VITE_USE_NEW_SYNC=true` in the environment.
   */
  useNewSync: import.meta.env.VITE_USE_NEW_SYNC === "true",

  /**
   * WS2 Phase 2a — window-priority hydration.
   *
   * When `true`, the sync engine prioritises hydrating the locally-known visible
   * window (top-K most-recently-active chats per list type + pinned + active
   * chat) on each run: window chats' new messages are backfilled first and their
   * last-message previews are decrypted BEFORE the multi-minute event drain,
   * instead of behind it. A pure ORDERING change inside the existing gated run —
   * no cursor/prune/endpoint changes (that is Phase 2b).
   *
   * Defaults to `false`; enable by setting `VITE_SYNC_WINDOWED_HYDRATION=true`
   * in the environment (staging → beta). No-op unless `useNewSync` is also on.
   */
  syncWindowedHydration:
    import.meta.env.VITE_SYNC_WINDOWED_HYDRATION === "true",

  /**
   * PR-A1 of the sync event-cursor deadlock fix (plan:
   * .plans/in-progress/sync-event-cursor-deadlock). Per-chat persisted event
   * cursors (`eventCursorsByChat` meta map) with mid-run checkpointing,
   * numeric `(ms,seq)` batch-`since` comparison (fixes the lexical stream-id
   * min that silently skips events — Bug #2), a bounded starved-chat
   * re-queue, and a visibility-aware run boundary for the `/sync/events`
   * drain.
   *
   * When `false` (default), the event drain is byte-identical to the previous
   * behavior (single global `lastEventCursor`, run-end-only persistence,
   * lexical batch min — including Bug #2, which is only fixed under the flag
   * so the whole change rides one kill switch). Rollback: turning the flag
   * off falls back to the legacy cursor, which v2 keeps writing
   * (ISO-normalized min over the per-chat values) — at worst one TTL-floored
   * replay. No Dexie schema change (meta-keyed map).
   *
   * Defaults to `false`; enable per environment with
   * `VITE_SYNC_EVENT_DRAIN_V2=true` (staging → prod). No-op unless
   * `useNewSync` is also on.
   */
  syncEventDrainV2: import.meta.env.VITE_SYNC_EVENT_DRAIN_V2 === "true",

  /**
   * Phase 1 — Jitsi conference-token minter cutover.
   *
   * When `true`, conference tokens are minted by the NEW Umbra minter in
   * `ow2-chat-be` (`POST /umbra/meet/conference-token`) instead of the legacy
   * Rust `register` minter (`register/conference-token`). The request contract
   * (`{ chat_id, chat_type }`) is identical on both endpoints — only the target
   * URL changes.
   *
   * Defaults to `false` (legacy `register` minter) so this is a no-op until
   * deliberately enabled via `VITE_USE_NEW_MINTER=true` in the environment.
   * Pairs with ow2-chat-be #218. The cutover is NOT validated until an
   * auth-ON staging test passes.
   */
  useNewMinter: import.meta.env.VITE_USE_NEW_MINTER === "true",

  /**
   * Forgot-username recovery flow (entry-point link + `/forgotUsername` route).
   *
   * When `false` (default), the login-screen link is hidden and the route
   * redirects to `/login`, so the live button is never shown against a backend
   * that can't yet send. Flip to `true` per environment by setting
   * `VITE_FORGOT_USERNAME_ENABLED=true` ONLY after that environment's mail
   * template env vars (MAIL_USERNAME_TEMPLATE / MAIL_USERNAME_SUBJECT) are set.
   */
  forgotUsername: import.meta.env.VITE_FORGOT_USERNAME_ENABLED === "true",

  /**
   * Account deletion v2 — `DELETE /umbra/v2/users/me` (ow2-chat-be
   * feat/delete-account-primitives). SHIPS-DARK, default OFF.
   *
   * When `false` (default), Settings renders the LEGACY delete flow
   * (`DELETE api/users` on the Rust backend) exactly as before — no
   * capability gap while the backend flag is off. Flip to `true` per
   * environment (`VITE_ACCOUNT_DELETION_V2=true`) to switch to the new
   * endpoint + rebuilt dialog (RFC 9457 ladder, 409 owned-groups blocked
   * state, full local wipe). NOTE: like every VITE_ env var this is BAKED IN
   * AT BUILD TIME — turning it off means a rebuild + release (or rolling
   * back to the previous tag), not a runtime flip. The runtime kill switch
   * is the backend's ACCOUNT_DELETION_ENABLED, whose 400 Account.NotEnabled
   * the dialog renders as a non-destructive "temporarily unavailable".
   *
   * Even with this flag ON, a backend whose own ACCOUNT_DELETION_ENABLED is
   * off returns 400 Account.NotEnabled, which the dialog renders as a
   * non-destructive "temporarily unavailable" state — the two flags need no
   * coordination.
   */
  accountDeletionV2: import.meta.env.VITE_ACCOUNT_DELETION_V2 === "true",

  /**
   * Change-email flow (Settings → Security → Change Email wizard).
   *
   * FE mirror of the backend `EMAIL_CHANGE_ENABLED` kill-switch (ow2-chat-be
   * #256). UNLIKE the ships-dark flags in this file, this gates a flow that
   * is ALREADY LIVE in production, so it is fail-safe default-ON: only the
   * exact string `false` disables it — a missing or malformed env value
   * keeps the feature up, so a config typo can't dark-kill a live flow.
   * Set `VITE_EMAIL_CHANGE_ENABLED=false` to hide the entry point; the
   * `errors.EmailChange.NotEnabled` handling inside the wizard remains the
   * runtime fallback for when the backend flips its switch first.
   */
  emailChange: import.meta.env.VITE_EMAIL_CHANGE_ENABLED !== "false",

  /**
   * Cloudflare Turnstile widget on the Login / Register / Forgot-Username
   * forms.
   *
   * FE mirror of the backend `TURNSTILE_ENABLED` flag. When `false`, the
   * widget is not rendered, the submit buttons are not gated on a Turnstile
   * token, and no `turnstileToken` is sent — the BE middleware ignores the
   * token entirely when its flag is off, so the pair must be flipped
   * TOGETHER: turning this off while the BE still requires tokens breaks
   * login/register with 403 Turnstile.Missing.
   *
   * Fail-safe default-ON (same reasoning as `emailChange`): only the exact
   * string `false` disables, so a missing/typo'd env value keeps the bot
   * protection up. This is the FE half of the emergency brake for a
   * Cloudflare widget outage — the BE's fail-open and TURNSTILE_GRACE_UNTIL
   * levers can't help while the FE still blocks submit on widget success.
   */
  turnstile: import.meta.env.VITE_TURNSTILE_ENABLED !== "false",

  /**
   * Guest-invite creation (host/moderator "invite guests" button + backend
   * `POST /umbra/meet/invites`).
   *
   * When `false` (default), `GuestInviteButton` is hidden everywhere — no
   * invite links can be created. Only the CREATE side is gated: the public
   * `/meet/guest` redeem route stays live unconditionally, since with
   * creation off no real invite codes exist yet to redeem, so leaving it
   * live is harmless and means the end-to-end flow needs no extra deploy
   * when this flag flips on.
   *
   * Defaults to `false`; enable by setting `VITE_GUEST_INVITES_ENABLED=true`
   * in the environment. Pairs with ow2-chat-be #218 — do not enable on an
   * environment until that backend PR is deployed there.
   */
  guestInvitesEnabled: import.meta.env.VITE_GUEST_INVITES_ENABLED === "true",

  /**
   * Orion in-call chat (the custom `orion-chat` toolbar button + `OrionChatPanel`
   * side panel that shows the room's Orion conversation inside a call).
   *
   * When `false` (default), the Orion in-call chat is OFF: the `orion-chat` custom
   * toolbar button is NOT injected, `OrionChatPanel` is NOT mounted, and Jitsi's
   * NATIVE `chat` button is added back to the toolbar so calls still have a chat.
   * When `true`, the current behavior is restored: the Orion chat button + panel are
   * shown and Jitsi's native chat stays hidden.
   *
   * Flagged OFF because the Orion in-call chat has two real defects: (a) minimizing
   * the call hides its input (thread-layout coupling), and (b) chats whose MAIN
   * THREAD restricts posting block the conference chat, since the conference chat IS
   * the main thread. We revert to Jitsi's native chat until the "meeting thread"
   * product design is defined. The Orion chat code (`OrionChatPanel`,
   * `useOrionChatButtonEvent`) is intentionally kept intact behind this flag.
   *
   * Open questions for the future "meeting thread" design: force-create a thread on
   * threadless groups, lock the thread on call-end, and P2P behavior.
   *
   * Defaults to `false`; enable per environment with
   * `VITE_ORION_INCALL_CHAT_ENABLED=true`.
   */
  orionInCallChat: import.meta.env.VITE_ORION_INCALL_CHAT_ENABLED === "true",

  /**
   * MASTER kill-switch for the whole Orion Meet in-call UI layer — the "jitsi
   * work" shipped since the last prod release (tag `v1.1.27`).
   *
   * When `false` (default), `Call.tsx` reproduces the v1.1.27 in-call
   * experience EXACTLY:
   *   - NO `customToolbarButtons` / `mainToolbarButtons` / `videoQuality`
   *     overrides in `configOverwrite`,
   *   - the original 25-button `TOOLBAR_BUTTONS` list (incl. `participants-pane`),
   *   - NO `onReadyToClose` return-to-chat teardown,
   *   - the floating Minimize overlay button (not the in-toolbar one),
   *   - the moderation + Orion-chat panels NOT mounted, and every meet-UI
   *     iframe hook (moderation events, toolbar-button events, moderator-only
   *     button re-apply, recording-consent toast) DISABLED (fed a null api),
   *   - the Recordings tab (`ChatDetailsTabs`) hidden even for GROUP moderators.
   * When `true`, the current Orion Meet in-call UI is active.
   *
   * This is ADDITIONAL to — and DOMINATES — `orionInCallChat` / `useNewMinter` /
   * `guestInvitesEnabled`: when this master flag is OFF the whole new toolbar +
   * panel layer is off regardless of those. It lets prod (beta) ship with the
   * flag OFF (old jitsi) while staging keeps it ON.
   *
   * Defaults to `false`; enable per environment with
   * `VITE_ORION_MEET_UI_ENABLED=true`.
   */
  orionMeetUi: import.meta.env.VITE_ORION_MEET_UI_ENABLED === "true",

  /**
   * Product B V0 — the STANDALONE meeting surface (`/meet/new` create page +
   * `/meet/:meetingId` join page), backed by the ow2-chat-be standalone
   * `meeting` records (ow2-chat-be#244: POST /umbra/meet/meetings + /token).
   *
   * SUBORDINATE to the `orionMeetUi` master flag: the standalone routes are
   * registered ONLY when BOTH flags are true (enforced in
   * `standaloneMeetRoutes.tsx`), so flipping the master kill-switch off takes
   * this surface down with the rest of the Orion Meet UI layer. When OFF, the
   * routes are not registered at all — no dead surface ships in prod bundles.
   *
   * Defaults to `false`; enabled per environment with
   * `VITE_STANDALONE_MEET_ENABLED=true` (staging only for now — do not enable
   * on an environment until ow2-chat-be >= 1.3.49 is deployed there).
   */
  standaloneMeet: import.meta.env.VITE_STANDALONE_MEET_ENABLED === "true",

  /**
   * Product-B analog of `guestInvitesEnabled` — gates the OWNER-side "copy
   * guest link" button on `MeetJoinPage` for STANDALONE meetings (ow2-chat-be
   * `POST /umbra/meet/meetings/invites`; redeem reuses the existing public
   * `/umbra/meet/invites/redeem` verbatim, so only the CREATE side is gated
   * here — same reasoning as `guestInvitesEnabled`).
   *
   * SUBORDINATE to `orionMeetUi && standaloneMeet`: the button lives inside
   * `MeetJoinPage`, which is only reachable when both of those are already on
   * (enforced in `standaloneMeetRoutes.tsx`), so this flag only needs to add
   * its own gate on top, not repeat theirs.
   *
   * Defaults to `false`; enable by setting
   * `VITE_MEETING_GUEST_INVITES_ENABLED=true` in the environment. Staging-only
   * for now — do not enable elsewhere until the backend create endpoint is
   * deployed there.
   */
  meetingGuestInvites:
    import.meta.env.VITE_MEETING_GUEST_INVITES_ENABLED === "true",

  /**
   * Guest-join pre-join name input (fe#1084).
   *
   * When `false` (default), `GuestMeetPage` is BYTE-IDENTICAL to today: the
   * mount redeem (`{ code }` only) happens exactly once and the guest is
   * dropped straight into the Jitsi embed — no pre-join screen. The optional
   * "Invitee name" field on `GuestInviteButton`'s owner dialog is also hidden
   * and no `inviteeName` is ever sent on invite creation.
   *
   * When `true`, `GuestMeetPage` shows a pre-join screen after the mount
   * redeem succeeds — a name input prefilled with the invite's owner-set
   * `inviteeName` (or blank if anonymous) — and re-redeems with
   * `displayName` ONLY if the guest edits the name before joining (the
   * unedited/blank case still uses the single mount-redeem token, so the
   * "exactly once" invariant holds whenever the name isn't changed). The
   * guest JWT's display name is always authoritative — set via `displayName`
   * on the redeem, never via a client-side Jitsi `userInfo` prop (the page
   * sets `disableProfile: true`, so Jitsi would ignore it anyway).
   *
   * Defaults to `false`; enable by setting `VITE_GUEST_NAME_INPUT_ENABLED=true`
   * in the environment. Pairs with the ow2-chat-be redeem/create contract that
   * added `inviteeName` (redeem response) and optional `inviteeName` (create
   * request) — do not enable on an environment until that backend change is
   * deployed there.
   */
  guestNameInput: import.meta.env.VITE_GUEST_NAME_INPUT_ENABLED === "true",

  /**
   * Orion Meet VANITY URL — claim + management (`/meet/vanity`) plus the root
   * shape-resolver that serves `staging.orionmessenger.io/<alias>` as a
   * standalone meeting join while KEEPING `<alias>` in the address bar.
   *
   * SUBORDINATE to `orionMeetUi && standaloneMeet` (mirrors `standaloneMeet`):
   * the root resolver only treats an alias-shaped segment as a vanity join, and
   * the `/meet/vanity` route only registers, when ALL THREE flags are on. When
   * OFF (default), the root `/:chatId?` route is BYTE-IDENTICAL to today (every
   * segment resolves to the chat experience / not-found bounce) and no vanity
   * surface ships.
   *
   * Defaults to `false`; enable per environment with
   * `VITE_MEET_VANITY_ENABLED=true` (staging only for now — do not enable until
   * the ow2-chat-be vanity endpoints, incl. GET /umbra/meet/meetings/mine, are
   * deployed there).
   */
  meetVanity: import.meta.env.VITE_MEET_VANITY_ENABLED === "true",

  /**
   * KDF v2/v3 (credential/KEK separation) — per-environment opt-in for the auth
   * key schedule.
   *
   * When `true`: new accounts register on v3 (authSecret credential + kek-wrapped
   * private key + per-user random salt), and existing v1/v2 accounts are
   * opportunistically upgraded to v3 on login (re-wrap under a fresh random salt).
   * When `false` (default): registration and login stay on the legacy v1 scheme
   * and no upgrade is attempted. Already-upgraded v2/v3 accounts still authenticate
   * regardless (the backend verify is credential-agnostic and login always reads
   * the account's kdf_version first) — this flag only governs whether *this client*
   * mints or advances v2/v3, never whether it can log such an account in.
   *
   * Ships DARK: unlike the always-live kill switches in this file (emailChange,
   * turnstile) which are fail-safe default-ON, v3 is a NEW credential-mutating
   * rollout with a strict back-to-front deploy order (see DEPLOY-KDF.md). Default
   * OFF makes an absent/forgotten env var a no-op, so v3 can never auto-enable
   * against a backend that can't yet serve it. Opt each environment IN with
   * `VITE_KDF_V2_ENABLED=true` only AFTER its Rust + node BE are confirmed live.
   */
  kdfV2: import.meta.env.VITE_KDF_V2_ENABLED === "true",

  /**
   * Settings middle-column views — the flat settings menu whose categories
   * (Security, Appearance, Language, Preferences) open as full middle-column
   * views instead of left-column accordion sections (docs/settings-middle-views/).
   *
   * When `true`: SettingsView renders the flat NavRow list, category views
   * render in the middle column (ChatUserAction.VIEW_SETTINGS_CATEGORY), and
   * the `/settings/:category` deep link is live.
   * When `false` (default): the legacy accordion renders unchanged, the deep
   * link redirects to `/`, and `isMiddlePaneAction()` refuses the settings
   * action — so a persisted VIEW_SETTINGS_CATEGORY from a previously flagged
   * build degrades to the empty state and is cleared by route sync, never
   * rendering the new UI in a flag-off build.
   *
   * Ships DARK: pure-FE presentational restructure, no BE dependency; default
   * OFF until the new UI is validated per environment.
   */
  settingsMiddleViews:
    import.meta.env.VITE_SETTINGS_MIDDLE_VIEWS_ENABLED === "true",
} as const;

export type FeatureFlags = typeof featureFlags;
